import './style.modules.scss'

import { Card, CardRecord } from '@ampcore/adel-react'
import { VideoPlayerProvider } from '@ampcore/video-player'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import xss from 'xss'

import { VideoCard } from '../VideoCard/VideoCard'
import type { VideoCardRecord } from '../VideoCard/VideoCardRecord.type'

function isCardVideoRecord(
  card: CardRecord | VideoCardRecord
): card is VideoCardRecord {
  return 'playlistId' in card
}

export type CardCarouselProps = {
  componentSpacing?: string
  cardList?: CardRecord[] | VideoCardRecord[]
  cardDescription?: string
  cardGridHeadline?: string
  testId?: string
  isRelatedInfo?: boolean
}

export const CardCarousel = (props: CardCarouselProps) => {
  const [showCarouselControls, setShowCarouselControls] = useState(true)
  const {
    componentSpacing,
    cardList,
    cardDescription,
    cardGridHeadline,
    testId,
    isRelatedInfo
  } = props
  const [position, setPosition] = useState(0)
  const [positionOffset, setPositionOffset] = useState(0)
  const [cardWidth, setCardWidth] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [startY, setStartY] = useState(0)
  const [startX, setStartX] = useState(0)
  const CARD_GAP = 20

  const moveLeft = useCallback(
    (jumpAmount: number) => {
      if (position === 0) {
        return
      }
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position - 1)
        return
      }
      if (position - jumpAmount <= 0) {
        setPosition(0)
        return
      }
      setPosition(position - jumpAmount)
    },
    [position]
  )
  const moveRight = useCallback(
    (jumpAmount: number) => {
      if (!cardList || position === cardList.length - 1) {
        return
      }
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position + 1)
        return
      }
      if (position - jumpAmount >= cardList.length) {
        setPosition(cardList.length - 1)
        return
      }
      setPosition(position - jumpAmount)
    },
    [position, cardList]
  )
  const onSwipe = useCallback(
    (deltaX: number, deltaY: number) => {
      if (Math.abs(deltaY) > 100) {
        return
      }
      const numCardsMoved = deltaX / (cardWidth + CARD_GAP)
      if (deltaX > 50) {
        moveLeft(Math.floor(numCardsMoved))
      }
      if (deltaX < -50) {
        moveRight(Math.ceil(numCardsMoved))
      }
    },
    [moveLeft, moveRight, cardWidth]
  )
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
      return
    }
    e.preventDefault()
    setIsSwiping(true)
    setStartX(e.touches[0].clientX)
    setStartY(e.touches[0].clientY)
  }, [])

  const handleTouch = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
        return
      }
      e.preventDefault()
      const endX = e.changedTouches[0].clientX
      const deltaX = endX - startX
      setPositionOffset(deltaX)
    },
    [startX, setPositionOffset]
  )

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
        return
      }
      e.preventDefault()
      const endX = e.changedTouches[0].clientX
      const endY = e.changedTouches[0].clientY
      const deltaX = endX - startX
      const deltaY = endY - startY
      onSwipe(deltaX, deltaY)
      setPositionOffset(0)
      setIsSwiping(false)
    },
    [startX, startY, onSwipe]
  )

  useEffect(() => {
    // Only observe overflow for the 'relatedInfo' variant to conditionally show carousel controls.
    if (!isRelatedInfo || !containerRef.current) return
    const wrapper = containerRef.current
    const observer = new ResizeObserver(() => {
      const timeoutId = setTimeout(() => {
        const hasOverflow = wrapper.scrollWidth > wrapper.clientWidth
        setShowCarouselControls(hasOverflow)
        if (!hasOverflow) setPosition(0)
      }, 100)
      return () => clearTimeout(timeoutId)
    })
    observer.observe(wrapper)
    return () => {
      observer.disconnect()
    }
  }, [isRelatedInfo])

  useEffect(() => {
    window.addEventListener('touchstart', handleTouchStart)
    window.addEventListener('touchmove', handleTouch)
    window.addEventListener('touchend', handleTouchEnd)
    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouch)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchEnd, handleTouch])

  const handleWindowSizeChange = () => {
    if (!wrapperRef || !wrapperRef.current) {
      return
    }
    const size = wrapperRef.current.getBoundingClientRect()
    if (size.width + 60 < 768) {
      setCardWidth(size.width - 20)
      return
    }
    if (size.width + 60 < 1240) {
      setCardWidth((size.width - 60) / 3)
      return
    }
    setCardWidth((size.width - 40) / 3)
  }

  useEffect(() => {
    handleWindowSizeChange()
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])

  if (!cardList) {
    return null
  }

  return (
    <section className={`card-carousel-component ${componentSpacing}`}>
      <div className="Grid u-sizeConstrained u-posRelative">
        {cardGridHeadline && (
          <h2
            className={`u-textColorDefault ${
              isRelatedInfo ? 'u-marginBottom0' : 'u-marginBottom16'
            }`}
          >
            {cardGridHeadline}
          </h2>
        )}
        {cardDescription && !isRelatedInfo && (
          <div
            className="card-description Content u-marginBottom20"
            dangerouslySetInnerHTML={{ __html: xss(cardDescription) }}
          />
        )}
      </div>
      <div
        ref={containerRef}
        className={`carousel-CardCarouselContainer--overflowWrapper u-paddingLeft32 u-paddingRight40 ${
          position !== 0
            ? 'shifted u-paddingLeft40 u-sm-paddingLeft40 u-md-paddingLeft40 '
            : ''
        } u-sizeFull u-sm-paddingRight40 u-md-paddingRight40 u-marginBottom4`}
      >
        <div
          className={`card-list-wrapper u-posRelative ${
            isRelatedInfo ? 'u-marginBottom24' : 'u-marginBottom20'
          }${isSwiping ? ' card-list-wrapper--noTransition' : ''}`}
          style={{
            gridTemplateColumns: `repeat(${cardList.length}, ${cardWidth}px)`,
            right: position * (cardWidth + CARD_GAP) - positionOffset
          }}
          ref={wrapperRef}
          data-testid={testId ? `${testId}-wrapper` : null}
        >
          <VideoPlayerProvider>
            {cardList?.map((card, index) =>
              isCardVideoRecord(card) ? (
                <VideoCard
                  key={'card' + index}
                  card={card}
                  onFocus={() => setPosition(index)}
                />
              ) : (
                <Card
                  key={index}
                  card={card}
                  onFocus={() => setPosition(index)}
                  isPersonalizationEnabled={isRelatedInfo ? true : false}
                />
              )
            )}
          </VideoPlayerProvider>
        </div>
      </div>
      {showCarouselControls && (
        <div className="card-carousel-controls u-flex u-flexAlignItemsEnd u-sizeConstrained u-marginBottom4 u-posRelative">
          <button
            className="Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke u-flex u-padding0"
            onClick={() => moveLeft(0)}
            disabled={position === 0}
            data-testid={testId ? `${testId}-slide-left` : null}
          >
            <span
              className="adel-material-symbols-24 u-textColorActionBlue"
              role="img"
              title="Slide Left"
              aria-label="Slide Left"
            >
              chevron_left
            </span>
          </button>
          <button
            className="Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke u-flex u-padding0"
            onClick={() => moveRight(0)}
            disabled={position === cardList.length - 1}
            data-testid={testId ? `${testId}-slide-right` : null}
          >
            <span
              className="adel-material-symbols-24 u-textColorActionBlue"
              role="img"
              title="Slide Right"
              aria-label="Slide Right"
            >
              chevron_right
            </span>
          </button>
          <p className="u-textColorActionBlue">
            {position + 1} / {cardList.length}
          </p>
        </div>
      )}
    </section>
  )
}
